/**
 * Thin typed web API client for the Search Detail performance report (`web/v2`).
 *
 * Separate from `searches.client.ts` (the My Searches list) because it is a
 * different feature with a different response shape and its own polling
 * lifecycle — colocating them would have made the list's client the owner of the
 * detail page's retry semantics.
 *
 * `202 Accepted` is a SUCCESS here, not an error: the body carries the search
 * header plus a `status` and a poll interval. So this client treats 200 and 202
 * identically and only throws for a real failure, which is what lets the page
 * render its title and spinner from the same code path as the finished report.
 *
 * Cookie auth rides the shared `makeAuthedFetch` lifecycle helper (single-flight
 * 401 → `POST /auth/refresh` → retry-once), the same contract every other web
 * client uses — a page that POLLS is exactly where a bare `fetch` would break,
 * since the 1h access-token cookie will expire mid-session on a long-lived tab.
 */

import type {
  SearchCandidateSortKey,
  SearchCandidateSortOrder,
  SearchCandidatesResponse,
  SearchReportExportFormat,
  SearchReportResponse,
} from "@nu-assist/contracts";
import { makeAuthedFetch } from "@nu-assist/shared-fe";

import type { CandidateReportKind } from "./candidateReports.client";

/** Minimal `fetch`-shaped function — `makeAuthedFetch(...)` by default. */
export type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface SearchReportClientConfig {
  /** API origin (e.g. `import.meta.env.VITE_API_URL`). */
  apiBase: string;
  /** Defaults to `makeAuthedFetch({ apiBase, onSessionExpired })`. Inject a
   *  plain mock in tests. */
  fetchImpl?: FetchLike;
}

export class SearchReportApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    message?: string
  ) {
    super(message ?? `Search report request failed (${status}) for ${url}`);
    this.name = "SearchReportApiError";
    Object.setPrototypeOf(this, SearchReportApiError.prototype);
  }
}

/** Thrown for a search that is not in the local mirror, so the page can show a
 *  "no such search" state instead of a generic failure. */
export class SearchNotFoundError extends SearchReportApiError {
  constructor(url: string) {
    super(404, url, "That search is not in nu-assist yet.");
    this.name = "SearchNotFoundError";
    Object.setPrototypeOf(this, SearchNotFoundError.prototype);
  }
}

/**
 * There is deliberately no 403 class here.
 *
 * Neither endpoint can emit one: the report module raises 404/409/422,
 * `require_user_email` answers 401 for every auth failure, the router carries no
 * router-level dependency, and the product decision is firm-wide visibility, so
 * no per-search entitlement model exists to fail. `openapi.json` declares
 * 200/202/401/404/422 and 200/401/404/409/422 accordingly.
 *
 * A 403 on these URLs can therefore only come from infrastructure — the shared
 * ALB, a WAF rule — where "you don't have access to this search's report" is a
 * confident authorization claim about a search the caller probably IS entitled
 * to, and treating it as permanently terminal stops the page recovering when the
 * rule is fixed. The generic failure copy is the honest answer for it. If an
 * ownership-scoped 403 ever ships, it gets a `responses={403: ...}` entry first
 * and this class comes back with it.
 */

/**
 * Thrown when an export is requested before any report has been composed.
 *
 * The READ endpoint answers that situation with 202 and a poll interval; the
 * export answers 409, because a file download has no loop to poll in. Its own
 * class so the page can say "not ready, try again shortly" instead of the
 * generic failure copy.
 *
 * `retryAfterSeconds` is 0 when the API withheld `Retry-After`, which is its way
 * of saying a timer will not help. A positive value means only that asking again
 * may help — NOT that a compose is running. The API's `detail` names which of the
 * three cases it is; this class deliberately does not, because prose written for
 * a developer is not UI copy.
 */
export class ReportNotReadyError extends SearchReportApiError {
  constructor(
    url: string,
    public readonly retryAfterSeconds: number
  ) {
    super(409, url, "This report is not ready to export yet.");
    this.name = "ReportNotReadyError";
    Object.setPrototypeOf(this, ReportNotReadyError.prototype);
  }
}

/**
 * Everything the All Candidates table can ask the server for.
 *
 * Filters and sort are SERVER-side, so this is the whole query: the browser
 * never holds more than one page, and never learns that the rows come from a
 * gzipped projection. Every field is optional and an omitted one is "not
 * filtering", which is what makes "clear all filters" an empty object rather
 * than a set of sentinel values.
 */
export interface SearchCandidatesQuery {
  page?: number;
  pageSize?: number;
  /**
   * Ask for the WHOLE filtered set in one response, unpaged.
   *
   * Export-only. The paged read exists so the browser never holds 1299 rows;
   * this is the one place that deliberately does, because a document is read
   * top-to-bottom and stitching fourteen requests together is fourteen chances
   * to end up silently short. Cannot be combined with `pageSize` — the API
   * answers 422.
   */
  all?: boolean;
  sort?: SearchCandidateSortKey;
  order?: SearchCandidateSortOrder;
  name?: string;
  title?: string;
  company?: string;
  /** Repeatable: ORs within the column, ANDs against every other filter. */
  stage?: readonly string[];
  highestStage?: readonly string[];
  hasLinkedin?: boolean;
  hasEmail?: boolean;
  /** Presence of a stored-report source in Thrive (sub-task 04): a recruiter
   *  assessment on the candidacy, official references for the search. */
  hasAssessment?: boolean;
  hasReferences?: boolean;
  /**
   * The report kinds the two filters above (and the row flags) count, sent as
   * `report_kinds=a,b`; omitted = the API's pre-epic default (the Recruiter
   * Assessment and the Official References). Since the four kinds were
   * released (2026-10-26) the web always sends all four
   * (`CANDIDATE_REPORT_KINDS`). The candidates read's query key sorts them,
   * so the order a caller lists them in does not split the cache.
   */
  reportKinds?: readonly CandidateReportKind[];
}

/**
 * The query as the wire spells it.
 *
 * Exported for its own test: the camelCase→snake_case mapping and the
 * repeatable `stage` are the two places this can silently send the wrong thing,
 * and a filter that quietly does not reach the server looks exactly like a
 * filter that matched everything.
 */
export function searchCandidatesParams(query: SearchCandidatesQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.page !== undefined) params.set("page", String(query.page));
  if (query.pageSize !== undefined) params.set("page_size", String(query.pageSize));
  // Only sent when true: `?all=false` would be an explicit request for the
  // default, and the API reads the parameter's PRESENCE alongside `page_size`.
  if (query.all) params.set("all", "true");
  if (query.sort) params.set("sort", query.sort);
  if (query.order) params.set("order", query.order);
  // Blank text is NOT sent: an empty `?name=` would be a filter the server has
  // to special-case, and this way clearing the box is indistinguishable from
  // never having typed in it.
  if (query.name?.trim()) params.set("name", query.name.trim());
  if (query.title?.trim()) params.set("title", query.title.trim());
  if (query.company?.trim()) params.set("company", query.company.trim());
  for (const stage of query.stage ?? []) params.append("stage", stage);
  for (const stage of query.highestStage ?? []) params.append("highest_stage", stage);
  if (query.hasLinkedin !== undefined) {
    params.set("has_linkedin", String(query.hasLinkedin));
  }
  if (query.hasEmail !== undefined) params.set("has_email", String(query.hasEmail));
  if (query.hasAssessment !== undefined) {
    params.set("has_assessment", String(query.hasAssessment));
  }
  if (query.hasReferences !== undefined) {
    params.set("has_references", String(query.hasReferences));
  }
  if (query.reportKinds !== undefined) params.set("report_kinds", query.reportKinds.join(","));
  return params;
}

/** A downloaded export plus the filename the SERVER chose for it. */
export interface ReportExportFile {
  blob: Blob;
  filename: string;
}

/**
 * Pull the filename out of a `Content-Disposition` header.
 *
 * Readable cross-origin only because the API sets
 * `Access-Control-Expose-Headers: Content-Disposition` — `Content-Disposition`
 * is not CORS-safelisted, so a browser that received it still reports `null`
 * without that. Hence the fallback: a plausible name beats a download saved as
 * a random UUID if the header ever stops arriving.
 *
 * Exported for its own test; the slug itself is deliberately NOT rebuilt here,
 * because two implementations of the same naming rule would drift.
 */
export function filenameFromDisposition(header: string | null, fallback: string): string {
  const match = header?.match(/filename="([^"]+)"/);
  return match?.[1] ?? fallback;
}

export function createSearchReportClient(cfg: SearchReportClientConfig) {
  // Built once, not per request. Safe eagerly at module-eval time because
  // `authedFetch` resolves `globalThis.fetch` at CALL time, so MSW's later
  // `server.listen()` patch is still honored.
  const authedFetch: FetchLike =
    cfg.fetchImpl ??
    makeAuthedFetch({
      apiBase: cfg.apiBase,
      onSessionExpired: () => window.location.assign("/login"),
    });

  return {
    /** GET /searches/{id}/report — 200 (report attached) or 202 (generating). */
    async getReport(searchId: string): Promise<SearchReportResponse> {
      const url = `${cfg.apiBase}/api/web/v2/searches/${encodeURIComponent(searchId)}/report`;
      const res = await authedFetch(url, {
        method: "GET",
        credentials: "include",
      });
      if (res.status === 404) {
        throw new SearchNotFoundError(url);
      }
      // 202 is expected on a cold search — `res.ok` covers 200-299, so this
      // check deliberately does NOT single out 200.
      if (!res.ok) {
        throw new SearchReportApiError(res.status, url);
      }
      return (await res.json()) as SearchReportResponse;
    },

    /**
     * GET /searches/{id}/report/candidates — one page of All Candidates.
     *
     * 202 is a SUCCESS here for the same reason it is on the report read: the
     * body carries a `status` and a poll interval, and the page needs to render
     * "building this table" rather than an error. What it must NOT do is render
     * the empty `items` as an empty table — a 202 means the projection has not
     * been composed, not that the search has no candidates.
     */
    async listCandidates(
      searchId: string,
      query: SearchCandidatesQuery = {}
    ): Promise<SearchCandidatesResponse> {
      const params = searchCandidatesParams(query);
      const suffix = params.toString();
      const url =
        `${cfg.apiBase}/api/web/v2/searches/${encodeURIComponent(searchId)}` +
        `/report/candidates${suffix ? `?${suffix}` : ""}`;
      const res = await authedFetch(url, {
        method: "GET",
        credentials: "include",
      });
      if (res.status === 404) {
        throw new SearchNotFoundError(url);
      }
      if (!res.ok) {
        throw new SearchReportApiError(res.status, url);
      }
      return (await res.json()) as SearchCandidatesResponse;
    },

    /**
     * DELETE /searches/{id}/report — discard the cached report.
     *
     * The Refresh button. It does not BUILD anything: the next `getReport`
     * misses the cache and enters the same 202-and-poll flow a first visit
     * does, which is why the page's job after this resolves is simply to
     * invalidate the query.
     *
     * A 404 gets its own class for the same reason the read does — the search
     * left the mirror, and "couldn't refresh" would be the wrong sentence for
     * a search that is gone.
     */
    async deleteReport(searchId: string): Promise<void> {
      const url = `${cfg.apiBase}/api/web/v2/searches/${encodeURIComponent(searchId)}/report`;
      const res = await authedFetch(url, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.status === 404) {
        throw new SearchNotFoundError(url);
      }
      if (!res.ok) {
        throw new SearchReportApiError(res.status, url);
      }
    },

    /**
     * GET /searches/{id}/report/export?format= — the report as a file.
     *
     * Fetched as a blob rather than navigated to with `<a download href>`: this
     * request needs the shared authed-fetch lifecycle (a 401 mid-session gets a
     * silent refresh and one retry), and an anchor navigation would instead
     * dump an auth error into the user's Downloads folder named `.csv`.
     */
    async exportReport(
      searchId: string,
      format: SearchReportExportFormat
    ): Promise<ReportExportFile> {
      const url =
        `${cfg.apiBase}/api/web/v2/searches/${encodeURIComponent(searchId)}` +
        `/report/export?format=${format}`;
      const res = await authedFetch(url, {
        method: "GET",
        credentials: "include",
      });
      if (res.status === 404) {
        throw new SearchNotFoundError(url);
      }
      if (res.status === 409) {
        throw new ReportNotReadyError(url, Number(res.headers.get("retry-after")) || 0);
      }
      if (!res.ok) {
        throw new SearchReportApiError(res.status, url);
      }
      return {
        blob: await res.blob(),
        filename: filenameFromDisposition(
          res.headers.get("content-disposition"),
          `search-${searchId}-report.${format}`
        ),
      };
    },
  };
}

export type SearchReportClient = ReturnType<typeof createSearchReportClient>;
